import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  serverTimestamp,
  query,
  where,
  getDocs,
  Timestamp,
  getDoc
} from 'firebase/firestore';

import { db } from '../firebase/config';
import { sendEmailTemplate } from '../utils/emailUtils';
import { EventRole, EventParticipantStatus } from '../types/Event';
import { RelationshipType } from '../types/Relationship';
import { getAuthHeaders } from '../utils/auth';
import axios from 'axios';

/**
 * Interface for Event Invitation
 */
export interface EventInvitation {
   eventId: string;
   eventTitle: string;
   inviterId: string;
   inviterName: string;
   email: string;
   role: EventRole;
   customMessage?: string;
   expiresAt?: Date;
   relationship?: {
      type: RelationshipType;
      label?: string | null;
      notes?: string | null;
   } | null;
   location?: string | null;
   status?: string;
   createdAt?: Date;
   acceptedAt?: Date;
   declinedAt?: Date;
   notificationCount?: number;
   lastNotifiedAt?: Date;
   id?: string;
   recipient_name?: string;
}

const INVITATIONS_COLLECTION = 'eventInvitations';
const EVENTS_COLLECTION = 'events';

/**
 * Send an event invitation to a user by email
 */
export const sendEventInvitation = async (invitation: EventInvitation): Promise<string> => {
   try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/event/invite`, {
         method: 'POST',
         headers,
         body: JSON.stringify({
            eventId: invitation.eventId,
            email: invitation.email,
            role: invitation.role,
            customMessage: invitation.customMessage,
            recipientName: invitation.recipient_name || '',
         }),
      });

      if (!response.ok) {
         const errorData = await response.json();
         throw new Error(errorData.error || 'Failed to send invitation');
      }

      const result = await response.json();
      return result.invitationId || 'invitation-sent';
   } catch (error) {
      console.error('Error sending event invitation:', error);
      throw error;
   }
};

/**
 * Resend an event invitation
 */
export const resendEventInvitation = async (invitationId: string): Promise<void> => {
   try {
      const invitationSnapshot = await getDoc(doc(db, 'eventInvitations', invitationId));

      if (!invitationSnapshot.exists()) {
         throw new Error('Invitation not found');
      }
      const invitation = invitationSnapshot.data();

      if (invitation.status === 'accepted') {
         throw new Error('Invitation has already been accepted');
      }

      // Update the invitation with new expiry date
      await updateDoc(doc(db, 'eventInvitations', invitationId), {
         expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
         updatedAt: new Date(),
      });

      // Resend the invitation using the dedicated resend endpoint
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/event/resend-invite`, {
         method: 'POST',
         headers,
         body: JSON.stringify({
            invitationId: invitationId,
         }),
      });

      if (!response.ok) {
         const errorData = await response.json();
         throw new Error(errorData.error || 'Failed to resend invitation');
      }

      const result = await response.json();
      console.log('Event invitation resent successfully:', result);
   } catch (error) {
      console.error('Error resending event invitation:', error);
      throw error;
   }
};

/**
 * Cancel an event invitation
 */
export const cancelEventInvitation = async (invitationId: string): Promise<void> => {
  try {
    //const invitationRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    //await deleteDoc(invitationRef);
  } catch (error) {
    console.error('Error canceling invitation:', error);
    throw new Error('Failed to cancel invitation');
  }
};

/**
 * Accept an event invitation
 */
export const acceptEventInvitation = async (invitationId: string, userId: string): Promise<void> => {
  try {
    const invitationRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    const invitationDoc = await getDoc(invitationRef);
    
    if (!invitationDoc.exists()) {
      throw new Error('Invitation not found');
    }

    const invitation = invitationDoc.data() as EventInvitation;
    
    if (invitation.status !== 'pending') {
      throw new Error('Invitation is no longer pending');
    }

    // Update invitation status
    await updateDoc(invitationRef, {
      status: 'accepted',
      acceptedAt: serverTimestamp(),
      respondedAt: serverTimestamp()
    });

    // Update event participants
    const eventRef = doc(db, EVENTS_COLLECTION, invitation.eventId);
    const eventDoc = await getDoc(eventRef);
    
    if (!eventDoc.exists()) {
      throw new Error('Event not found');
    }

    const event = eventDoc.data();
    const participants = event.participants || [];
    
    // Add user to participants if not already present
    if (!participants.some((p: any) => p.email === invitation.email)) {
      participants.push({
        userId: userId,
        email: invitation.email,
        role: invitation.role,
        status: 'accepted',
        invitedBy: invitation.inviterId,
        invitedAt: invitation.createdAt,
        respondedAt: serverTimestamp()
      });

      await updateDoc(eventRef, { participants });
    }
  } catch (error) {
    console.error('Error accepting event invitation:', error);
    throw error;
  }
};

/**
 * Decline an event invitation
 */
export const declineEventInvitation = async (invitationId: string): Promise<void> => {
  try {
    const invitationRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    const invitationDoc = await getDoc(invitationRef);
    
    if (!invitationDoc.exists()) {
      throw new Error('Invitation not found');
    }

    await updateDoc(invitationRef, {
      status: 'declined',
      declinedAt: serverTimestamp(),
      respondedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error declining event invitation:', error);
    throw error;
  }
};

/**
 * Get all events a user has been invited to
 */
export const getUserEventInvitations = async (userEmail: string): Promise<any[]> => {
  try {
    const invitationsQuery = query(
      collection(db, INVITATIONS_COLLECTION),
      where('email', '==', userEmail.toLowerCase()),
      where('status', '==', 'pending')
    );
    
    const snapshot = await getDocs(invitationsQuery);
    
    const invitations: any[] = [];
    snapshot.forEach(doc => {
      invitations.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return invitations;
  } catch (error) {
    console.error('Error fetching user event invitations:', error);
    throw new Error('Failed to load invitations');
  }
};

const checkExistingInvitation = async (eventId: string, email: string): Promise<EventInvitation | null> => {
  const q = query(
    collection(db, INVITATIONS_COLLECTION),
    where('eventId', '==', eventId),
    where('email', '==', email.toLowerCase())
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as EventInvitation;
};

const sendInvitationEmail = async (invitation: EventInvitation): Promise<void> => {
  const emailData = {
    to: invitation.email,
    subject: `You're invited to ${invitation.eventTitle}`,
    templateName: 'event-invitation',
    templateData: {
      eventTitle: invitation.eventTitle,
      inviterName: invitation.inviterName,
      customMessage: invitation.customMessage,
      inviteLink: `${process.env.REACT_APP_BASE_URL}/events/invite/${invitation.id}`
    }
  };

  await sendEmailTemplate(emailData);
}; 